import { push } from "connected-react-router";
import { put, fork, takeLatest } from "redux-saga/effects";
import { setError, setSuccess, startLoading } from "../store/appSlice";
import {
  fetchCashManagementNotificationsSettings,
  setCashNotificationsSettings,
  updateCashManagementNotificationsSettings,
  createCashManagementNotificationsSettings,
  cashManagementNotificationsOperationPaths,
  setCashManagementNotificationsSettingsActiveFlag,
} from "../store/cashManagementNotificationSlice";
import { UUID } from "../utils";
import * as CashNotifications from "../api/CashManagementNotifications";
import { callApi } from "./helpers/api";
import { CashManagementNotificationSettings } from "../models/CashManagement";

function* fetchNotificationsSettingsSaga({
  payload,
}: {
  payload: {
    id: UUID;
  };
  type: string;
}) {
  const { id } = payload;
  const { details: operationPath } = cashManagementNotificationsOperationPaths(
    id
  );
  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(
      CashNotifications.getCashManagementNotificationsSettings,
      id
    );
    yield put(setCashNotificationsSettings({ id, item: data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateNotificationsSettingsSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: any;
  };
  type: string;
}) {
  const { id, data } = payload;
  const { update: operationPath } = cashManagementNotificationsOperationPaths(
    id
  );
  yield put(startLoading(operationPath));

  try {
    const notificationsSettings = yield callApi(
      CashNotifications.putCashManagementNotificationsSettings,
      id,
      data
    );
    yield put(
      setCashNotificationsSettings({ id, item: notificationsSettings })
    );
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${id}/cash-management`));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createNotificationsSettingsSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: any;
  };
  type: string;
}) {
  const { id, data } = payload;

  const { create: operationPath } = cashManagementNotificationsOperationPaths(
    id
  );
  yield put(startLoading(operationPath));

  try {
    const notificationsSettings = yield callApi(
      CashNotifications.postCashManagementNotificationsSettings,
      id,
      data
    );
    yield put(
      setCashNotificationsSettings({ id, item: notificationsSettings })
    );
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/customers/${id}/cash-management`));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setNotificationsSettingsActiveFlagSaga({
  payload,
}: {
  payload: {
    id: UUID;
    data: CashManagementNotificationSettings;
  };
  type: string;
}) {
  const { id, data } = payload;

  const { active: operationPath } = cashManagementNotificationsOperationPaths(
    id
  );
  yield put(startLoading(operationPath));

  try {
    const notificationsSettings = yield callApi(
      CashNotifications.putCashManagementNotificationsSettings,
      id,
      data
    );
    yield put(
      setCashNotificationsSettings({ id, item: notificationsSettings })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchNotificationsSettings() {
  yield takeLatest(
    fetchCashManagementNotificationsSettings.type,
    fetchNotificationsSettingsSaga
  );
}

function* watchUpdateNotificationsSettings() {
  yield takeLatest(
    updateCashManagementNotificationsSettings.type,
    updateNotificationsSettingsSaga
  );
}

function* watchCreateNotificationsSettings() {
  yield takeLatest(
    createCashManagementNotificationsSettings.type,
    createNotificationsSettingsSaga
  );
}
function* watchNotificationsSettingsActiveFlag() {
  yield takeLatest(
    setCashManagementNotificationsSettingsActiveFlag.type,
    setNotificationsSettingsActiveFlagSaga
  );
}

export function* CashNotificationsSaga() {
  yield fork(watchFetchNotificationsSettings);
  yield fork(watchUpdateNotificationsSettings);
  yield fork(watchCreateNotificationsSettings);
  yield fork(watchNotificationsSettingsActiveFlag);
}
